
/**
 * Feature Flag System
 * 
 * Provides a simple mechanism to enable/disable features during refactoring.
 * Features can be toggled via localStorage or URL parameters.
 */

export type FeatureFlag =
  | 'enable_new_api_layer'
  | 'enable_react_query'
  | 'enable_new_auth'
  | 'enable_new_stripe_webhook'
  | 'enable_sms';

/**
 * Check if a feature flag is enabled
 * 
 * Priority:
 * 1. URL parameter 'enable_all=true' enables all features
 * 2. URL parameter for specific flag (e.g., 'enable_react_query=true')
 * 3. localStorage value
 * 4. Default to false
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  // FIXME: Temporarily return true for all feature flags during debugging
  return true;
}

/**
 * Set a feature flag value in localStorage
 */
export function setFeatureFlag(flag: FeatureFlag, value: boolean): void {
  try {
    localStorage.setItem(`feature_flag_${flag}`, value.toString());
    console.log(`Feature flag ${flag} set to ${value}`);
  } catch (e) {
    console.error(`Error setting feature flag ${flag}:`, e);
  }
}

/**
 * Reset all feature flags to their default values (disabled)
 */
export function resetFeatureFlags(): void {
  const allFlags: FeatureFlag[] = [
    'enable_new_api_layer',
    'enable_react_query',
    'enable_new_auth',
    'enable_new_stripe_webhook',
    'enable_sms'
  ];
  
  allFlags.forEach(flag => {
    try {
      localStorage.removeItem(`feature_flag_${flag}`);
    } catch (e) {
      console.error(`Error resetting feature flag ${flag}:`, e);
    }
  });
  
  console.log('All feature flags have been reset');
}

/**
 * Get the status of all feature flags
 */
export function getAllFeatureFlags(): Record<FeatureFlag, boolean> {
  const allFlags: FeatureFlag[] = [
    'enable_new_api_layer',
    'enable_react_query',
    'enable_new_auth',
    'enable_new_stripe_webhook',
    'enable_sms'
  ];
  
  return allFlags.reduce((acc, flag) => {
    acc[flag] = isFeatureEnabled(flag);
    return acc;
  }, {} as Record<FeatureFlag, boolean>);
}
